import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, X, Sparkles, ArrowRight, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * First-day checklist — only visible to the platform owner (you), since
 * setting up branding, SMTP, Twilio sender ID etc. is part of the £2,500
 * onboarding service we deliver, not the dealer's job. Dealers see a
 * pre-configured workshop on day one and never have to think about this.
 *
 * Lives at the top of the dashboard for platform-owner accounts only.
 */
export default function OnboardingChecklist() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  // User can re-expand a server-auto-collapsed card with one click. The
  // override is per-session — once they reload, the auto-collapsed pill is
  // back, because that's the desired calm state on day-50+.
  const [expandOverride, setExpandOverride] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/onboarding/state");
      setState(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Bail early for non-platform-owner accounts — dealers shouldn't see this.
    if (!user?.platform_owner) { setLoading(false); return; }
    load();
    // Re-fetch every 60s so a completed task (e.g. teammate invited) ticks
    // without a hard refresh.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user]);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/onboarding/dismiss", { dismissed: true });
      setState((s) => ({ ...s, dismissed: true }));
      toast.success("Onboarding hidden — you can re-open from Settings");
    } catch { toast.error("Couldn't dismiss"); }
    finally { setDismissing(false); }
  };

  if (loading) return null;
  if (!user?.platform_owner) return null;
  if (!state) return null;
  if (state.dismissed) return null;
  if (state.completed) return (
    <div className="card-default p-5 border-success/30 bg-success/[0.04]" data-testid="onboarding-completed">
      <div className="flex items-center gap-3">
        <span className="inline-flex w-9 h-9 rounded-full bg-success/20 items-center justify-center">
          <Sparkles className="w-4 h-4 text-success" />
        </span>
        <div className="flex-1">
          <h3 className="font-display font-bold text-base">You're all set up 🎉</h3>
          <p className="text-xs text-muted-foreground">All 5 setup steps complete. Your workshop is ready for customers.</p>
        </div>
        <button onClick={dismiss} disabled={dismissing} className="p-2 rounded-full hover:bg-muted" aria-label="Hide" data-testid="onboarding-completed-dismiss">
          {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  const pct = Math.round((state.progress / state.total) * 100);

  // Auto-collapsed pill for older workshops — calm one-liner that won't
  // dominate the dashboard, but still gives a one-click "show details" out.
  if (state.auto_collapsed && !expandOverride) {
    return (
      <button
        type="button"
        onClick={() => setExpandOverride(true)}
        className="w-full text-left card-default p-3 border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors flex items-center gap-3"
        data-testid="onboarding-collapsed-pill"
        title="Show setup checklist"
      >
        <span className="inline-flex w-7 h-7 rounded-full bg-primary/15 items-center justify-center flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
        </span>
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <span className="text-sm font-semibold">Workshop setup</span>
          <span className="text-xs text-muted-foreground">{state.progress} / {state.total} done</span>
          <div className="hidden sm:flex flex-1 max-w-xs h-1 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-primary">
          Show details <ChevronDown className="w-3 h-3" />
        </span>
      </button>
    );
  }

  return (
    <div className="card-default p-5 border-primary/40 bg-gradient-to-br from-primary/[0.06] to-primary/[0.02]" data-testid="onboarding-checklist">
      <div className="flex items-start gap-3">
        <span className="inline-flex w-10 h-10 rounded-full bg-primary/15 items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="label-overline text-primary">Welcome to ServiceSell PRO&trade;</div>
          <h3 className="font-display font-black text-xl mt-0.5">Get your workshop live in {state.total} steps</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {state.progress} of {state.total} done · auto-ticks as you go
          </p>
        </div>
        <button onClick={dismiss} disabled={dismissing}
                className="p-1.5 rounded-full hover:bg-muted text-muted-foreground"
                aria-label="Hide checklist"
                data-testid="onboarding-dismiss-btn"
                title="Hide for now">
          <X className="w-4 h-4" />
        </button>
        {/* When the user opened an auto-collapsed pill, give them a single
            click to put it back to the calm pill state for this session. */}
        {state.auto_collapsed && expandOverride && (
          <button onClick={() => setExpandOverride(false)}
                  className="p-1.5 rounded-full hover:bg-muted text-muted-foreground"
                  aria-label="Collapse"
                  data-testid="onboarding-collapse-btn"
                  title="Collapse">
            <ChevronUp className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} data-testid="onboarding-progress-bar" />
      </div>

      <ul className="mt-4 space-y-2">
        {state.tasks.map((t, idx) => (
          <li key={t.id}
              className={`flex items-start gap-3 rounded-lg p-3 transition-colors ${
                t.done ? "bg-success/[0.05]" : "bg-card hover:bg-muted/40"
              }`}
              data-testid={`onboarding-task-${t.id}`}
              data-done={t.done ? "1" : "0"}>
            <span className="flex-shrink-0 mt-0.5">
              {t.done
                ? <CheckCircle2 className="w-5 h-5 text-success" data-testid={`onboarding-task-${t.id}-done`} />
                : <Circle className="w-5 h-5 text-muted-foreground" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${t.done ? "line-through text-muted-foreground" : ""}`}>
                <span className="text-muted-foreground font-mono text-[10px] mr-2">{String(idx+1).padStart(2,"0")}</span>
                {t.label}
              </div>
              {!t.done && (
                <p className="text-xs text-muted-foreground mt-0.5">{t.helper}</p>
              )}
            </div>
            {!t.done && t.action_path && (
              <Link
                to={t.action_path}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:scale-[1.02] transition-transform flex-shrink-0"
                data-testid={`onboarding-task-${t.id}-action`}
              >
                {t.action_label || "Start"} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            {!t.done && !t.action_path && t.id === "install_pwa" && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("pwa:show-prompt"))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:scale-[1.02] transition-transform flex-shrink-0"
                data-testid={`onboarding-task-${t.id}-action`}
              >
                {t.action_label || "Install"} <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
